import React, { useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Input,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Table,
} from "reactstrap";
import SectionState from "./SectionState";
import { AuditoriaUsuarioDto } from "../../../types/ajustes";

interface AuditoriaTabProps {
  eventos: AuditoriaUsuarioDto[];
  loading: boolean;
  error: string | null;
  onReload: () => void;
}

const getBadgeColor = (accion: string) => {
  const texto = accion.toLowerCase();
  if (texto.includes("crea") || texto.includes("alta")) return "success";
  if (texto.includes("elim") || texto.includes("baja")) return "danger";
  if (texto.includes("login") || texto.includes("acceso")) return "info";
  if (texto.includes("actualiza") || texto.includes("modifica")) return "warning";
  return "secondary";
};

const AuditoriaTab = ({ eventos, loading, error, onReload }: AuditoriaTabProps) => {
  const [selected, setSelected] = useState<AuditoriaUsuarioDto | null>(null);
  const [search, setSearch] = useState("");
  const [filterAction, setFilterAction] = useState("all");
  const [filterUser, setFilterUser] = useState("");

  const formatter = useMemo(
    () =>
      new Intl.DateTimeFormat("es-PY", {
        dateStyle: "short",
        timeStyle: "medium",
      }),
    []
  );

  const eventosOrdenados = useMemo(() => {
    return [...eventos].sort((a, b) => {
      const fechaA = new Date(a.fechaIso).getTime();
      const fechaB = new Date(b.fechaIso).getTime();
      return (Number.isNaN(fechaB) ? 0 : fechaB) - (Number.isNaN(fechaA) ? 0 : fechaA);
    });
  }, [eventos]);

  const filteredEventos = useMemo(() => {
    return eventosOrdenados.filter((evento) => {
      if (filterAction !== "all") {
        const textoAccion = (evento.accion ?? "").toLowerCase();
        if (!textoAccion.includes(filterAction)) {
          return false;
        }
      }
      if (
        filterUser &&
        !(evento.ejecutadoPor ?? "sin usuario").toLowerCase().includes(filterUser.toLowerCase())
      ) {
        return false;
      }
      if (!search) {
        return true;
      }
      const termino = search.toLowerCase();
      return (
        (evento.descripcion ?? "").toLowerCase().includes(termino) ||
        (evento.entidad ?? "").toLowerCase().includes(termino) ||
        (evento.ejecutadoPor ?? "sin usuario").toLowerCase().includes(termino)
      );
    });
  }, [eventosOrdenados, filterAction, filterUser, search]);

  const stats = useMemo(() => {
    const acumulados: Record<string, number> = {
      creaciones: 0,
      bajas: 0,
      logins: 0,
      cambios: 0,
    };
    eventosOrdenados.forEach((evento) => {
      const texto = (evento.accion ?? "").toLowerCase();
      if (texto.includes("crea") || texto.includes("alta")) {
        acumulados.creaciones += 1;
      } else if (texto.includes("elim") || texto.includes("baja")) {
        acumulados.bajas += 1;
      } else if (texto.includes("login") || texto.includes("acceso")) {
        acumulados.logins += 1;
      } else if (texto.includes("actualiza") || texto.includes("modifica")) {
        acumulados.cambios += 1;
      }
    });
    return acumulados;
  }, [eventosOrdenados]);

  const formatDate = (iso: string) => {
    const fecha = new Date(iso);
    if (Number.isNaN(fecha.getTime())) return "Sin fecha";
    return formatter.format(fecha);
  };

  const renderMetadata = (metadata?: Record<string, unknown> | null) => {
    if (!metadata) return null;
    const entries = Object.entries(metadata);
    if (entries.length === 0) return null;
    return (
      <div className="border rounded p-2 bg-light mb-2">
        <div className="fw-semibold text-muted mb-1">Metadatos</div>
        <dl className="mb-0 small">
          {entries.slice(0, 8).map(([clave, valor]) => (
            <div key={clave} className="d-flex justify-content-between gap-2">
              <dt className="text-uppercase text-muted">{clave}:</dt>
              <dd className="mb-0">
                {typeof valor === "object" && valor !== null
                  ? JSON.stringify(valor)
                  : String(valor)}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    );
  };

  const renderDetalle = (
    datos?: Record<string, unknown> | null,
    opciones?: { destacado?: "antes" | "despues" }
  ) => {
    if (!datos) return <p className="text-muted">Sin datos</p>;
    const entries = Object.entries(datos);
    if (entries.length === 0) return <p className="text-muted">Sin datos</p>;
    const resaltado =
      opciones?.destacado === "antes"
        ? "table-danger"
        : opciones?.destacado === "despues"
        ? "table-success"
        : "";
    return (
      <div className="mb-3 border rounded bg-white">
        <div className="px-3 py-2 fw-semibold text-uppercase text-muted small">
          Datos {opciones?.destacado || ""}
        </div>
        <div className="overflow-auto" style={{ maxHeight: 280 }}>
          <Table bordered size="sm" className={`mb-0 ${resaltado}`}>
            <tbody>
              {entries.map(([clave, valor]) => (
                <tr key={clave}>
                  <th className="w-25 text-uppercase text-muted small">{clave}</th>
                  <td className="small">
                    {typeof valor === "object" && valor !== null
                      ? JSON.stringify(valor)
                      : String(valor)}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>
    );
  };

  const renderCambios = (cambios?: AuditoriaUsuarioDto["cambios"]) => {
    if (!cambios || cambios.length === 0) return null;
    return (
      <div className="mb-3">
        <div className="fw-semibold text-muted mb-1">Campos modificados</div>
        <Table bordered size="sm">
          <thead>
            <tr>
              <th>Campo</th>
              <th>Antes</th>
              <th>Después</th>
            </tr>
          </thead>
          <tbody>
            {cambios.map((cambio) => (
              <tr key={cambio.campo}>
                <td className="text-uppercase text-muted">{cambio.campo}</td>
                <td>{String(cambio.antes ?? "—")}</td>
                <td>{String(cambio.despues ?? "—")}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  return (
    <Card>
      <CardBody>
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
          <div>
            <CardTitle tag="h5" className="mb-1">
              Auditoría de usuarios
            </CardTitle>
            <p className="text-muted mb-0">
              Revisa las últimas acciones realizadas sobre cuentas de usuario.
            </p>
          </div>
          <Button color="light" size="sm" onClick={onReload} disabled={loading}>
            Actualizar
          </Button>
        </div>
        <div className="d-flex flex-wrap gap-2 mb-3">
          {[
            { label: "Creaciones", value: stats.creaciones },
            { label: "Bajas", value: stats.bajas },
            { label: "Logins", value: stats.logins },
            { label: "Cambios", value: stats.cambios },
          ].map((item) => (
            <Card key={item.label} className="flex-grow-1 text-center" style={{ minWidth: 140 }}>
              <CardBody className="py-2">
                <div className="text-muted text-uppercase small">{item.label}</div>
                <div className="fw-semibold fs-4">{item.value}</div>
              </CardBody>
            </Card>
          ))}
        </div>
        <Row className="g-2 mb-3">
          <Col md={5}>
            <Input
              placeholder="Buscar por usuario, entidad o descripción"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </Col>
          <Col md={3}>
            <Input type="select" value={filterAction} onChange={(event) => setFilterAction(event.target.value)}>
              <option value="all">Todas las acciones</option>
              <option value="crea">Creaciones</option>
              <option value="elim">Bajas</option>
              <option value="login">Logins</option>
              <option value="actualiza">Actualizaciones</option>
            </Input>
          </Col>
          <Col md={3}>
            <Input
              placeholder="Filtrar por usuario"
              value={filterUser}
              onChange={(event) => setFilterUser(event.target.value)}
            />
          </Col>
          <Col md={1} className="d-grid">
            <Button
              color="light"
              size="sm"
              onClick={() => {
                setSearch("");
                setFilterAction("all");
                setFilterUser("");
              }}
            >
              Limpiar
            </Button>
          </Col>
        </Row>

        <SectionState
          loading={loading}
          error={error}
          description="Cargando auditorías..."
          onRetry={onReload}
        />

        {!loading && !error && (
          <div className="table-responsive">
            <Table className="align-middle">
              <thead className="table-light">
                <tr>
                  <th>Fecha</th>
                  <th>Entidad</th>
                  <th>Acción</th>
                  <th>Ejecutado por</th>
                  <th>IP/Origen</th>
                  <th>Detalle</th>
                  <th className="text-center">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredEventos.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-4">
                      {search || filterUser || filterAction !== "all"
                        ? "No hay coincidencias para el filtro seleccionado."
                        : "No se registraron acciones recientemente."}
                    </td>
                  </tr>
                )}
                {filteredEventos.map((evento) => (
                  <tr key={String(evento.id)}>
                    <td className="text-nowrap text-muted">{formatDate(evento.fechaIso)}</td>
                    <td>
                      <div className="fw-semibold">
                        {evento.entidad ?? evento.usuario ?? "Sin entidad"}
                      </div>
                      {evento.modelo && (
                        <small className="text-muted text-uppercase">
                          {evento.modelo}
                          {evento.modeloId ? ` #${evento.modeloId}` : ""}
                        </small>
                      )}
                    </td>
                    <td>
                      <Badge color={getBadgeColor(evento.accion)}>{evento.accion}</Badge>
                    </td>
                    <td>{evento.ejecutadoPor ?? "No disponible"}</td>
                    <td>{evento.ip ?? "Sin dato"}</td>
                    <td>{evento.descripcion ?? "Sin detalle adicional."}</td>
                    <td className="text-center">
                      <Button color="link" size="sm" onClick={() => setSelected(evento)}>
                        Ver detalles
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </CardBody>

      <Modal isOpen={Boolean(selected)} toggle={() => setSelected(null)} size="lg">
        <ModalHeader toggle={() => setSelected(null)}>Detalle de auditoría</ModalHeader>
        <ModalBody>
          {selected && (
            <>
              <div className="mb-3">
                <div className="fw-semibold">Acción</div>
                <p className="mb-1">{selected.accion}</p>
                <small className="text-muted">{formatDate(selected.fechaIso)}</small>
              </div>
              <div className="mb-3">
                <div className="fw-semibold">Descripción</div>
                <p>{selected.descripcion ?? "Sin descripción disponible."}</p>
              </div>
              {renderMetadata(selected.metadata)}
              <div className="mb-3">
                <div className="fw-semibold">Datos antes</div>
                {renderDetalle(selected.datosAntes, { destacado: "antes" })}
              </div>
              <div className="mb-3">
                <div className="fw-semibold">Datos después</div>
                {renderDetalle(selected.datosDespues, { destacado: "despues" })}
              </div>
              {renderCambios(selected.cambios)}
            </>
          )}
        </ModalBody>
      </Modal>
    </Card>
  );
};

export default AuditoriaTab;
